// Theme preference: "light" | "dark" | "system". The resolved theme is written to
// <html data-theme>. public/index.html runs the same resolution inline before
// first paint so there is no flash of the wrong theme.
import { useEffect, useState } from "react";

const STORAGE_KEY = "voorent-theme";
const THEME_COLORS = { light: "#ffffff", dark: "#060b13" };
const listeners = new Set();

function readPreference() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch (e) {
    return "system";
  }
}

function systemTheme() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function resolve(preference) {
  return preference === "system" ? systemTheme() : preference;
}

function apply(preference) {
  const theme = resolve(preference);
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[theme]);
  listeners.forEach((listener) => listener(preference, theme));
}

export function setThemePreference(preference) {
  try {
    if (preference === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch (e) {
    // Storage can be unavailable (private mode); the choice still applies for this visit.
  }
  apply(preference);
}

export function initTheme() {
  apply(readPreference());
  if (window.matchMedia) {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (readPreference() === "system") apply("system");
    };
    if (query.addEventListener) query.addEventListener("change", onChange);
    else if (query.addListener) query.addListener(onChange);
  }
}

export function useTheme() {
  const [state, setState] = useState(() => {
    const preference = readPreference();
    return { preference, theme: resolve(preference) };
  });

  useEffect(() => {
    const listener = (preference, theme) => setState({ preference, theme });
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  return state;
}
